import type { DisputePlaybook } from "@/api/deduction.tsx"
import { BookOpen, ChevronDown, ChevronRight, ExternalLink, Mail } from "lucide-react"
import { useState } from "preact/hooks"

interface DisputePlaybookPanelProps {
  playbook: DisputePlaybook
  className?: string
}

function ContactRow({ label, email }: { label: string; email: string }) {
  return (
    <div class="flex items-center gap-2 text-sm">
      <Mail className="h-3.5 w-3.5 shrink-0 text-slate-500" />
      <span class="text-slate-500 shrink-0">{label}</span>
      <a href={`mailto:${email}`} class="text-blue-700 hover:underline break-all">
        {email}
      </a>
    </div>
  )
}

function StepList({ title, steps }: { title: string; steps: string[] }) {
  if (steps.length === 0) return null
  return (
    <div>
      <div class="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">{title}</div>
      <ol class="list-decimal pl-5 space-y-1 text-sm text-slate-700">
        {steps.map((step: string, i: number) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
    </div>
  )
}

/**
 * "How to dispute" — the retailer's published process for this deduction: who to email, which
 * portal to file in, how to pull backup, and what the invoice prefix means. Collapsed by default
 * to a one-line summary so it reads as reference, not a chore.
 */
export function DisputePlaybookPanel({ playbook, className }: DisputePlaybookPanelProps) {
  const [open, setOpen] = useState(false)
  const summary =
    playbook.channel === "email" && playbook.dispute_email
      ? `Disputes go by email to ${playbook.dispute_email}`
      : `Disputes are filed in ${playbook.portal_name ?? `the ${playbook.retailer} portal`}`
  const Chevron = open ? ChevronDown : ChevronRight

  return (
    <div class={`border border-slate-200 rounded-lg overflow-hidden ${className ?? ""}`}>
      <button
        type="button"
        class="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-left hover:bg-slate-100 transition-colors"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}>
        <BookOpen className="h-4 w-4 text-slate-600 shrink-0" />
        <div class="min-w-0 flex-1">
          <div class="text-sm font-medium text-slate-800">How to dispute at {playbook.retailer}</div>
          <div class="text-xs text-slate-500 truncate">
            {playbook.deduction_type ? `${playbook.deduction_type} · ` : ""}
            {summary}
          </div>
        </div>
        <Chevron className="h-4 w-4 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div class="px-4 py-3 space-y-3">
          {playbook.deduction_type && (
            <div class="text-sm">
              <span class="text-slate-500">This deduction: </span>
              <span class="text-slate-800">{playbook.deduction_type}</span>
            </div>
          )}
          {(playbook.dispute_email || playbook.backup_email || playbook.portal_url) && (
            <div class="space-y-1">
              {playbook.dispute_email && (
                <ContactRow label={playbook.channel === "email" ? "Disputes" : "Contact"} email={playbook.dispute_email} />
              )}
              {playbook.backup_email && playbook.backup_email !== playbook.dispute_email && (
                <ContactRow label="Backup" email={playbook.backup_email} />
              )}
              {playbook.portal_url && (
                <div class="flex items-center gap-2 text-sm">
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                  <span class="text-slate-500 shrink-0">Portal</span>
                  <a href={playbook.portal_url} target="_blank" rel="noreferrer" class="text-blue-700 hover:underline break-all">
                    {playbook.portal_name ?? playbook.portal_url}
                  </a>
                </div>
              )}
            </div>
          )}
          <StepList title="Filing the dispute" steps={playbook.steps} />
          <StepList title="Getting backup" steps={playbook.backup_steps} />
          {playbook.notes.length > 0 && (
            <ul class="list-disc pl-5 space-y-1 text-xs text-slate-500">
              {playbook.notes.map((note: string, i: number) => (
                <li key={i}>{note}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
